import React from 'react';

import { CardListSkeleton } from '@eika-kortbetjening/card-list-panel';
import { DetailList } from '@eika-kortbetjening/detail-list';

import { useCard } from '@/react-query/hooks/query/use-card';
import { useDebitProductSpendingLimits } from '@/react-query/hooks/query/use-spending-limits';

import { Card } from '@generated-types';
import useText, { KortbetjeningText } from '@hooks/use-text';
import { isChildCard, isTeenagerCard } from '@utils/card-type-utils';
import { prettyMonetary } from '@utils/text-format-utils';

import { CouldNotFetchProductSpendingLimits } from './CouldNotFetchProductSpendingLimits';

const getHeader = (card: Card, text: KortbetjeningText) => {
    if (isChildCard(card)) {
        return text.spendingLimitLimitationChild;
    }

    if (isTeenagerCard(card)) {
        return text.spendingLimitLimitationTeenager;
    }

    return text.spendingLimitLimitationGeneral;
};

const ProductSpendingLimits = () => {
    const text = useText();
    const { data: card } = useCard();
    const { data: productSpendingLimits, isLoading, isSuccess, isError } = useDebitProductSpendingLimits(card);

    if (typeof card === 'undefined' || isLoading) {
        return <CardListSkeleton />;
    }

    if (isError) {
        return <CouldNotFetchProductSpendingLimits />;
    }

    if (isSuccess && productSpendingLimits) {
        if (!productSpendingLimits.card && !productSpendingLimits.app) {
            return null;
        }

        // eslint-disable-next-line react/no-unstable-nested-components
        const SpendingLimits = () => {
            if (productSpendingLimits.card) {
                const { daily, monthly } = productSpendingLimits.card;

                return (
                    <>
                        <DetailList.Item
                            description={text.spendingLimitLimitationDayDescription}
                            value={daily ? prettyMonetary(daily) : ''}
                        />

                        <DetailList.Item
                            description={text.spendingLimitLimitationMonthDescription}
                            value={monthly ? prettyMonetary(monthly) : ''}
                        />
                    </>
                );
            }

            return null;
        };

        // eslint-disable-next-line react/no-unstable-nested-components
        const SpendingLimitsEcommerce = () => {
            if (productSpendingLimits.app) {
                const { daily, monthly } = productSpendingLimits.app;

                return (
                    <>
                        <DetailList.Item
                            description={text.spendingLimitLimitationAppDayDescription}
                            value={daily ? prettyMonetary(daily) : ''}
                        />

                        <DetailList.Item
                            description={text.spendingLimitLimitationAppMonthDescription}
                            value={monthly ? prettyMonetary(monthly) : ''}
                        />
                    </>
                );
            }

            return null;
        };

        return (
            <DetailList header={getHeader(card, text)}>
                <SpendingLimits />
                <SpendingLimitsEcommerce />
            </DetailList>
        );
    }

    return null;
};

export default ProductSpendingLimits;
